import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'store/recipientQueryTemplate';
import { AssociatedCampaignsModalDialog } from 'views/UserMessaging/customModalContent/associatedCampaigns';
import { ToolbarV2, TableComponentV2 } from 'components';
import filterHelpers from 'store/helpers/filterHelpers';
import { recipientQueriesTableTitles } from 'constants.js';
import { useHistory } from 'react-router-dom';
import { produce } from 'immer';
import { mapExpressionFromResposne } from 'store/helpers';
import { resetListsData } from 'store/lists/actions';
import CopyTemplateModal from 'components/Template/CopyTemplateModal';

const {
  getPaginatedQueryTemplates,
  deleteQueryTemplates,
  unsetCurrentQueryTemplate,
  setCurrentQueryTemplate,
  warnAndArchiveQueryTemplate,
  updateRecipientQueryFilters,
  resetRecipientQueryFilters
} = actions;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  }
}));

const RecipientQueries = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    onClickToggleArchive,
    onClickAdd,
    onClickEdit,
    isTemplateReadOnly,
    isTenantDefaultTemplate
  } = filterHelpers();
  const action = getPaginatedQueryTemplates({ force: true });
  const recipientQueries = useSelector(
    store => store.queryTemplate.paginatedList
  );
  const filters = useSelector(
    store => store.queryTemplate.filters
  );
  const isLoading = useSelector(store => store.queryTemplate.gettingPaginated);
  const productNames = useSelector(
    store => store.events.supportedEventTypes.transientProductNames
  );
  const productCategories = useSelector(
    store => store.events.supportedEventTypes.transientProductCategories
  );
  const optionsToFilterTemplate = [
    {
      name: 'searchScopes',
      searchScope: 'TENANT DEFAULT',
      value: 'tenant',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'PROPERTY LEVEL',
      value: 'property',
      checked: false
    }
  ];
  const optionsToFilterProductNames = productNames
    ? productNames.map(productName => {
        return { name: 'productName', value: productName, checked: false };
      })
    : [];
  const optionsToFilterProductCategories = productCategories
    ? productCategories.map(productCategory => {
        return {
          name: 'productCategory',
          value: productCategory,
          checked: false
        };
      })
    : [];

  const [openCopyTemplateModal, setOpenCopyTemplateModal] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    dispatch(getPaginatedQueryTemplates({ force: true }));
    dispatch(resetListsData());
  }, []);

  const onClickArchive = uuid => {
    let action = deleteQueryTemplates({
      templateUuids: [uuid],
      activeStatus: filters?.displayActive
    });
    let warnMessage = {
      buttons: [
        { title: 'Archive Recipient Query', action: ['DISPATCH', action] },
        { title: 'Cancel', action: 'DISMISS' }
      ],
      customContent: AssociatedCampaignsModalDialog
    };
    if (filters?.displayActive) {
      dispatch(warnAndArchiveQueryTemplate({ uuid: uuid, message: warnMessage }));
    } else {
      dispatch(action);
    }
  };

  const handleImportClick = () => {
    setOpenCopyTemplateModal(true);
  };

  const getButtonsGroup = () => [
    {
      name: `View ${filters?.displayActive ? 'Archived' : 'Active'} Queries`,
      action: () => onClickToggleArchive(action, updateRecipientQueryFilters, { displayActive: !filters?.displayActive, pageNo: 0 }),
    },
    {
      name: 'Add Recipient Query',
      action: () =>
        onClickAdd(unsetCurrentQueryTemplate(), `/recipient-queries/details`)
    },
    {
      name: 'Import',
      action: () => handleImportClick()
    }
  ];

  const getActionButtons = queryTemplate => [
    {
      name: isTemplateReadOnly(queryTemplate, isLoading)
        ? 'View Query'
        : 'Edit Query',
      action: ['ACTION', () => onEdit(queryTemplate)],
      ...(isTemplateReadOnly(queryTemplate, isLoading) && {
        tooltipPlacement: 'left-start',
        tooltipTitle: isTenantDefaultTemplate(queryTemplate)
          ? "Tenant Default template can be edited only with 'ALL' property"
          : 'Only admin can edit Global Default template'
      })
    },
    {
      name: 'Copy Query',
      action: ['ACTION', () => onCopy(queryTemplate.uuid)]
    },
    {
      name: filters?.displayActive ? 'Archive Query' : 'Activate Query',
      action: ['ACTION', () => onClickArchive(queryTemplate.uuid)],
      ...(isTemplateReadOnly(queryTemplate, isLoading) && {
        isDisabled: true,
        tooltipPlacement: 'left-start',
        tooltipTitle: isTenantDefaultTemplate(queryTemplate)
          ? "Tenant Default template can be edited only with 'ALL' property"
          : 'Only admin can edit Global Default template'
      })
    }
  ];

  const filterOptions = {
    filters: [
      {
        options: optionsToFilterTemplate.map(option => {
          if (filters?.filterTemplates?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Hierarchy',
        values: filters?.filterTemplates,
        onChange: templates => dispatch(updateRecipientQueryFilters({ filterTemplates: templates, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductNames.map(option => {
          if (filters?.filterProductNames?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Name',
        values: filters?.filterProductNames,
        onChange: productNames =>
          dispatch(updateRecipientQueryFilters({ filterProductNames: productNames, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductCategories.map(option => {
          if (filters?.filterProductCategories?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Category',
        values: filters?.filterProductCategories,
        onChange: productCategories =>
          dispatch(updateRecipientQueryFilters({ filterProductCategories: productCategories, pageNo: 0 }))
      }
    ],
    resetFilters: () => dispatch(resetRecipientQueryFilters())
  };

  const searchOptions = {
    searchPlaceholder: 'Search Recipient Queries',
    searchAction: (payload) => dispatch(updateRecipientQueryFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  const onEdit = row =>
    onClickEdit(
      [unsetCurrentQueryTemplate(), setCurrentQueryTemplate(row)],
      `/recipient-queries/details/${row.uuid}`
    );
  const onCopy = uuid => {
    let template = Object.assign(
      {},
      recipientQueries.find(t => t.uuid === uuid)
    );
    delete template.id;
    delete template.uuid;
    template._isCopy = true;
    template.name = template.name + '-copy';
    template.active = true;
    template = produce(template, draftTemplate => {
      draftTemplate.conditionExpression = mapExpressionFromResposne(
        draftTemplate?.conditionExpression || {}
      );
      if (draftTemplate.hasOwnProperty('_hasSetTemplate')) {
        draftTemplate._hasSetTemplate = true;
      }
    });
    dispatch(setCurrentQueryTemplate(template));
    history.push(`/recipient-queries/details`);
  };

  return (
    <div className={classes.root}>
      <ToolbarV2
        searchOptions={searchOptions}
        filterOptions={filterOptions}
        buttonsGroup={getButtonsGroup()}
        action={getPaginatedQueryTemplates({ force: true })}
      />
      <div className={classes.content}>
        <TableComponentV2
          isLoading={isLoading}
          tableTitles={recipientQueriesTableTitles}
          onDoubleClickRow={row => onEdit(row)}
          actionButtonOptions={row => getActionButtons(row)}
          filteredRows={recipientQueries}
          action={getPaginatedQueryTemplates({ force: true })}
          filterValues={filters}
          filterAction={(payload) => dispatch(updateRecipientQueryFilters(payload))}
        />
      </div>
      {openCopyTemplateModal && (
        <CopyTemplateModal
          isOpen={openCopyTemplateModal}
          closeModal={() => setOpenCopyTemplateModal(false)}
        />
      )}
    </div>
  );
};

export default RecipientQueries;
